// Playbook Library REST helpers — FE-044.

import { api } from "@/api/client";

import type {
  CreatePlaybookInput,
  PlaybookBlocksRefreshResponse,
  PlaybookListResponse,
  PlaybookListRow,
  PlaybookStatusFilter,
} from "./types";


export async function fetchPlaybookList(
  status: PlaybookStatusFilter,
  opts?: { signal?: AbortSignal },
): Promise<PlaybookListResponse> {
  const sp = new URLSearchParams();
  sp.set("status", status);
  return api.get<PlaybookListResponse>(
    `/api/v1/playbooks/?${sp.toString()}`,
    opts,
  );
}


export function createPlaybook(
  input: CreatePlaybookInput,
): Promise<PlaybookListRow> {
  return api.post<PlaybookListRow>("/api/v1/playbooks/", {
    ...input,
    applicable_scopes: input.applicable_scopes ?? ["account"],
  });
}


export function updatePlaybook(
  playbookId: string,
  input: { name: string },
): Promise<PlaybookListRow> {
  return api.patch<PlaybookListRow>(
    `/api/v1/playbooks/${encodeURIComponent(playbookId)}`,
    input,
  );
}


export function fetchPlaybookDetail(
  playbookId: string,
  opts?: { signal?: AbortSignal },
): Promise<PlaybookListRow> {
  return api.get<PlaybookListRow>(
    `/api/v1/playbooks/${encodeURIComponent(playbookId)}`,
    opts,
  );
}


export function refreshPlaybookBlocks(
  playbookId: string,
  blocksJson: unknown,
): Promise<PlaybookBlocksRefreshResponse> {
  return api.post<PlaybookBlocksRefreshResponse>(
    `/api/v1/playbooks/${encodeURIComponent(playbookId)}/blocks/refresh`,
    { blocks_json: blocksJson },
  );
}


export interface PlaybookCommitmentCategoryOption {
  slug: string;
  display_name: string;
}


export interface PlaybookCommitmentTagOption {
  id: string;
  slug: string;
  display_name: string;
  display_color: string | null;
  is_regulatory: boolean;
}


export function fetchCommitmentCategoryOptions(
  opts?: { signal?: AbortSignal },
): Promise<{ categories: PlaybookCommitmentCategoryOption[] }> {
  return api.get<{ categories: PlaybookCommitmentCategoryOption[] }>(
    "/api/v1/commitments/pickers/categories",
    opts,
  );
}


export function fetchCommitmentTagOptions(
  opts?: { signal?: AbortSignal },
): Promise<{ tags: PlaybookCommitmentTagOption[] }> {
  return api.get<{ tags: PlaybookCommitmentTagOption[] }>(
    "/api/v1/commitments/pickers/tags",
    opts,
  );
}


export function duplicatePlaybook(
  playbookId: string,
): Promise<PlaybookListRow> {
  return api.post<PlaybookListRow>(
    `/api/v1/playbooks/${encodeURIComponent(playbookId)}/duplicate`,
  );
}


export function archivePlaybook(
  playbookId: string,
): Promise<PlaybookListRow> {
  return api.post<PlaybookListRow>(
    `/api/v1/playbooks/${encodeURIComponent(playbookId)}/archive`,
  );
}


export function restorePlaybook(
  playbookId: string,
): Promise<PlaybookListRow> {
  return api.post<PlaybookListRow>(
    `/api/v1/playbooks/${encodeURIComponent(playbookId)}/restore`,
  );
}


export function deletePlaybook(playbookId: string): Promise<void> {
  return api.delete<void>(
    `/api/v1/playbooks/${encodeURIComponent(playbookId)}`,
  );
}


// ── BE-046 — Apply Playbook ──────────────────────────────────────────────


export interface ApplyPlaybookInput {
  account_id: string;
  plan_year_id: string;
  dry_run?: boolean;
  confirm_duplicates?: boolean;
}


export interface ApplyPlaybookCreatedCommitment {
  id: string;
  commitment_number: string | null;
  title: string;
  status: string;
  due_date_time: string | null;
  source_block_uid: string | null;
}


export interface ApplyPlaybookDuplicateCandidate {
  existing_commitment_id: string;
  title: string;
  source_block_uid: string | null;
}


export interface ApplyPlaybookResponse {
  // Success branch:
  application_id?: string;
  playbook_id?: string;
  account_id?: string;
  plan_year_id?: string | null;
  applied_at?: string | null;
  created_count?: number;
  created_commitments?: ApplyPlaybookCreatedCommitment[];
  receipt_token_id?: string;
  undo_expires_at?: string;
  warnings?: string[];
  // Duplicate-warning branch:
  requires_confirmation?: boolean;
  duplicate_application_id?: string;
  duplicate_applied_at?: string | null;
  duplicate_candidates?: ApplyPlaybookDuplicateCandidate[];
  message?: string;
  // Dry-run branch:
  dry_run?: boolean;
  would_create_count?: number;
  preview?: Array<{
    source_block_uid: string;
    title: string;
    category_slug: string;
    category_name: string;
    priority: string;
    complexity: number;
    due_date_time: string | null;
    depends_on_block_uids: string[];
  }>;
}


export function applyPlaybook(
  playbookId: string,
  input: ApplyPlaybookInput,
): Promise<ApplyPlaybookResponse> {
  return api.post<ApplyPlaybookResponse>(
    `/api/v1/playbooks/${encodeURIComponent(playbookId)}/apply`,
    input,
  );
}


export interface RevertPlaybookApplicationResponse {
  application_id: string;
  reverted_count: number;
  revoked_at: string | null;
}


export function revertPlaybookApplication(
  applicationId: string,
): Promise<RevertPlaybookApplicationResponse> {
  return api.post<RevertPlaybookApplicationResponse>(
    `/api/v1/playbooks/applications/${encodeURIComponent(applicationId)}/revert`,
    {},
  );
}


// ── FE-047 — list recent applications ────────────────────────────────────


export interface PlaybookApplicationRow {
  application_id: string;
  playbook_id: string;
  account_id: string;
  account_name: string | null;
  plan_year_id: string | null;
  plan_year_label: string | null;
  plan_year_start: string | null;
  plan_year_end: string | null;
  applied_at: string | null;
  applied_by_id: string | null;
  applied_by_name: string | null;
  applied_by_email: string | null;
  created_count: number;
  revoked_at: string | null;
  undo_expires_at: string | null;
  can_undo: boolean;
}


export interface PlaybookApplicationsListResponse {
  rows: PlaybookApplicationRow[];
  total: number;
}


export function fetchPlaybookApplications(
  playbookId: string,
  opts?: { signal?: AbortSignal; limit?: number },
): Promise<PlaybookApplicationsListResponse> {
  const sp = new URLSearchParams();
  if (opts?.limit) sp.set("limit", String(opts.limit));
  const qs = sp.toString();
  return api.get<PlaybookApplicationsListResponse>(
    `/api/v1/playbooks/${encodeURIComponent(playbookId)}/applications${
      qs ? `?${qs}` : ""
    }`,
    opts,
  );
}
